import { ICredentials } from '../../../../interfaces';
import { ServerTime } from '../../infrastructure/server-time/server-time';
import { IServerTime } from '../../infrastructure/server-time/server-time.interface';
import { InvalidAccessTokenError } from '../errors/invalid-access-token-error';
import { CredentialsPayload } from './credentials-payload';

export class Credentials implements ICredentials {
	private serverTime: IServerTime = new ServerTime();

	public static create(credentials: { accessToken: string; refreshToken: string }): Credentials {
		return new Credentials(credentials.accessToken, credentials.refreshToken);
	}

	constructor(
		public readonly accessToken: string = '',
		public readonly refreshToken: string,
	) {}

	public isNewerThan(credentials: Credentials): boolean {
		const credentialsPayload = this.getPayload();
		const comparingPayload = credentials.getPayload();

		return credentialsPayload.exp > comparingPayload.exp;
	}

	public getPayload(): CredentialsPayload {
		return this.getPayloadFromJWT(this.accessToken);
	}

	public async isExpired(): Promise<boolean> {
		if (this.accessToken === null) {
			return true;
		}

		return this.isUnixTimestampExpired(
			this.getPayload().exp,
			await this.serverTime.now()
		);
	}

	public isRefreshTokenExpired(): boolean {
		if (this.refreshToken === null) {
			return true;
		}

		return this.isUnixTimestampExpired(
			this.getPayloadFromJWT(this.refreshToken).exp,
			new Date().getTime()
		);
	}

	public toJSON(): { accessToken: string; refreshToken: string } {
		return {
			accessToken: this.accessToken,
			refreshToken: this.refreshToken,
		};
	}

	private getPayloadFromJWT(jwt: string): CredentialsPayload {
		try {
			const tokenPayloadUrlSafe = jwt.split('.')[1];
			const tokenPayloadBase64 = tokenPayloadUrlSafe.replace(/-/g, '+').replace(/_/g, '/');
			const tokenPayload = JSON.parse(window.atob(tokenPayloadBase64));
			return tokenPayload as CredentialsPayload;
		} catch (error) {
			console.error('Failed to parse access token:', error);
			throw new InvalidAccessTokenError('Not a valid JWT.');
		}
	}

	private isUnixTimestampExpired(unixTimestamp: number, comparisonTime: number): boolean {
		// 10 seconds before the token expire, we consider it expired
		const expiryTime = (unixTimestamp - 10) * 1000;

		return expiryTime <= comparisonTime;
	}
}
